import { supabase } from '@/lib/supabase';

export interface TradingFile {
  id: string;
  filename: string;
  file_size: number;
  total_trades: number;
  win_trades: number;
  loss_trades: number;
  win_rate: number;
  uploaded_by: string;
  uploaded_at: string;
  processed_at?: string;
  status: 'uploaded' | 'processing' | 'processed' | 'error';
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface TradingPattern {
  id: string;
  file_id: string;
  pattern: string;
  pattern_length: number;
  frequency: number;
  next_win_count: number;
  next_loss_count: number;
  success_rate: number;
  confidence_score: number;
  last_seen_at: string;
  created_at: string;
  updated_at: string;
}

export interface PatternPrediction {
  id: string;
  user_id: string;
  input_sequence: string;
  sequence_length: number;
  predicted_win_probability: number;
  predicted_loss_probability: number;
  matching_patterns_count: number;
  confidence_level: 'very_low' | 'low' | 'medium' | 'high' | 'very_high';
  prediction_method: string;
  actual_result?: number;
  is_correct?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CSVTradeData {
  'Trade #': string;
  'Type': string;
  'Signal': string;
  'Date/Time': string;
  'Price USD': string;
  'Quantity': string;
  'P&L USD': string;
  'P&L %': string;
  'Run-up USD': string;
  'Run-up %': string;
  'Drawdown USD': string;
  'Drawdown %': string;
  'Cumulative P&L USD': string;
  'Cumulative P&L %': string;
}

export interface PatternAnalysisConfig {
  minPatternLength: number;
  maxPatternLength: number;
  minFrequency: number;
}

export interface DetectedPattern {
  pattern: string;
  frequency: number;
  nextWinCount: number;
  nextLossCount: number;
  successRate: number;
  confidenceScore: number;
}

export interface AnalysisHistoryItem {
  analysis_mode: string;
  created_at: string;
  pattern_count: number;
  avg_confidence: number;
  processing_time: number;
  high_risk_count: number;
  opportunity_count: number;
}

export class TradingPatternAnalysisService {
  /**
   * Save uploaded CSV file metadata to database
   */
  async saveTradingFile(fileData: {
    filename: string;
    file_size: number;
    total_trades: number;
    win_trades: number;
    loss_trades: number;
  }): Promise<{ success: boolean; data?: TradingFile; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('trading_files')
        .insert([{
          ...fileData,
          status: 'uploaded'
        }])
        .select()
        .single();

      if (error) {
        console.error('Error saving trading file:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in saveTradingFile:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Update trading file status
   */
  async updateTradingFileStatus(
    fileId: string, 
    status: TradingFile['status'], 
    errorMessage?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData: any = { 
        status,
        updated_at: new Date().toISOString()
      };

      if (status === 'processed') {
        updateData.processed_at = new Date().toISOString();
      }

      if (errorMessage) {
        updateData.error_message = errorMessage;
      }

      const { error } = await supabase
        .from('trading_files')
        .update(updateData)
        .eq('id', fileId);

      if (error) {
        console.error('Error updating trading file status:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in updateTradingFileStatus:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Save detected patterns to database
   */
  async savePatterns(
    fileId: string, 
    patterns: DetectedPattern[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const patternData = patterns.map(pattern => ({
        file_id: fileId,
        pattern: pattern.pattern,
        pattern_length: pattern.pattern.length,
        frequency: pattern.frequency,
        next_win_count: pattern.nextWinCount,
        next_loss_count: pattern.nextLossCount,
        success_rate: pattern.successRate,
        confidence_score: pattern.confidenceScore
      }));

      const { error } = await supabase
        .from('trading_patterns')
        .insert(patternData);

      if (error) {
        console.error('Error saving patterns:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in savePatterns:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get trading files for current user (static method)
   */
  static async getTradingFiles(): Promise<{ success: boolean; data?: TradingFile[]; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('trading_files')
        .select('*')
        .eq('uploaded_by', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching trading files:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get patterns for a specific file (static method)
   */
  static async getPatterns(fileId: string): Promise<{ success: boolean; data?: TradingPattern[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('trading_patterns')
        .select('*')
        .eq('file_id', fileId)
        .order('confidence_score', { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching patterns:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Delete a trading file and all its associated data (static method)
   * Option 2: Prevent deletion if active bots are linked to the file
   */
  static async deleteTradingFile(fileId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Check if any bots are linked to this trading file
      const { data: linkedBots, error: botCheckError } = await supabase
        .from('bots')
        .select('id, name, signal_token')
        .eq('linked_trading_file_id', fileId);

      if (botCheckError) {
        console.error('Error checking linked bots:', botCheckError);
        return { success: false, error: botCheckError.message };
      }

      // Prevent deletion if there are active bots linked to this file
      if (linkedBots && linkedBots.length > 0) {
        const botNames = linkedBots.map(bot => bot.name).join(', ');
        return { 
          success: false, 
          error: `Không thể xóa file này vì đang có ${linkedBots.length} bot(s) đang sử dụng: ${botNames}. Vui lòng unlink các bot trước khi xóa file.` 
        };
      }

      // If no linked bots, proceed with deletion
      // Only delete the trading_files record - CASCADE will handle the rest
      const { error: fileError } = await supabase
        .from('trading_files')
        .delete()
        .eq('id', fileId);

      if (fileError) {
        console.error('Error deleting trading file:', fileError);
        return { success: false, error: fileError.message };
      }

      console.log(`✅ Successfully deleted trading file ${fileId} and all associated data via CASCADE`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting trading file:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get trading files for current user (instance method)
   */
  async getTradingFiles(limit = 20): Promise<{ success: boolean; data?: TradingFile[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('trading_files')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching trading files:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getTradingFiles:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get patterns for a specific file (instance method)
   */
  async getPatterns(fileId: string): Promise<{ success: boolean; data?: TradingPattern[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('trading_patterns')
        .select('*')
        .eq('file_id', fileId)
        .order('frequency', { ascending: false });

      if (error) {
        console.error('Error fetching patterns:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getPatterns:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Save a pattern prediction
   */
  async savePrediction(predictionData: {
    input_sequence: string;
    predicted_win_probability: number;
    predicted_loss_probability: number;
    matching_patterns_count: number;
    confidence_level: PatternPrediction['confidence_level'];
    prediction_method: string;
  }): Promise<{ success: boolean; data?: PatternPrediction; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('pattern_predictions')
        .insert([{
          ...predictionData,
          sequence_length: predictionData.input_sequence.split(',').length
        }])
        .select()
        .single();

      if (error) {
        console.error('Error saving prediction:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in savePrediction:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Convert CSV trade data to binary sequence (1 for win, 0 for loss)
   */
  convertToBinarySequence(trades: CSVTradeData[]): number[] {
    return trades.map(trade => {
      const pnl = parseFloat(trade['P&L USD'].replace(/[^-\d.]/g, ''));
      return pnl > 0 ? 1 : 0; // 1 for win (positive only), 0 for loss (negative or zero)
    });
  }

  /**
   * Analyze patterns in a binary sequence
   */
  analyzePatterns(sequence: number[], config: PatternAnalysisConfig): DetectedPattern[] {
    const patterns = new Map<string, { frequency: number; nextResults: number[] }>();
    
    // Extract all patterns of specified lengths
    for (let length = config.minPatternLength; length <= config.maxPatternLength; length++) {
      for (let i = 0; i <= sequence.length - length - 1; i++) {
        const pattern = sequence.slice(i, i + length).join('');
        const nextResult = sequence[i + length];
        
        if (nextResult !== undefined) {
          if (!patterns.has(pattern)) {
            patterns.set(pattern, { frequency: 0, nextResults: [] });
          }
          
          const patternData = patterns.get(pattern)!;
          patternData.frequency++;
          patternData.nextResults.push(nextResult);
        }
      }
    }
    
    // Filter by minimum frequency and calculate statistics
    return Array.from(patterns.entries())
      .filter(([_, data]) => data.frequency >= config.minFrequency)
      .map(([pattern, data]) => {
        const winCount = data.nextResults.filter(r => r === 1).length;
        const lossCount = data.nextResults.filter(r => r === 0).length;
        const successRate = winCount / (winCount + lossCount) * 100;
        
        return {
          pattern,
          frequency: data.frequency,
          nextWinCount: winCount,
          nextLossCount: lossCount,
          successRate: Math.round(successRate * 100) / 100,
          confidenceScore: this.calculateConfidenceScore(data.frequency, successRate)
        };
      })
      .sort((a, b) => b.frequency - a.frequency);
  }

  /**
   * Calculate confidence score based on frequency and success rate
   */
  private calculateConfidenceScore(frequency: number, successRate: number): number {
    if (frequency >= 10 && successRate >= 70) return 90;
    if (frequency >= 5 && successRate >= 60) return 75;
    if (frequency >= 3 && successRate >= 50) return 60;
    if (frequency >= 2) return 40;
    return 20;
  }

  /**
   * Predict next trade based on input sequence
   */
  async predictNextTrade(
    inputSequence: string,
    fileId?: string
  ): Promise<{
    success: boolean;
    prediction?: {
      winProbability: number;
      lossProbability: number;
      confidenceLevel: PatternPrediction['confidence_level'];
      matchingPatterns: number;
    };
    error?: string;
  }> {
    try {
      const sequence = inputSequence.split(',').map(x => parseInt(x.trim()));
      
      // Validate sequence
      if (sequence.some(x => isNaN(x) || (x !== 0 && x !== 1))) {
        return { success: false, error: 'Invalid sequence format' };
      }

      // Build query to find matching patterns
      let query = supabase
        .from('trading_patterns')
        .select('*');

      if (fileId) {
        query = query.eq('file_id', fileId);
      }

      const { data: allPatterns, error } = await query;

      if (error) {
        console.error('Error fetching patterns for prediction:', error);
        return { success: false, error: error.message };
      }

      if (!allPatterns || allPatterns.length === 0) {
        return { 
          success: false, 
          error: 'No patterns available for prediction. Please upload and process CSV files first.' 
        };
      }

      // Find patterns that match the end of the input sequence
      const inputString = sequence.join('');
      const matchingPatterns = allPatterns.filter(pattern => {
        return inputString.endsWith(pattern.pattern) || pattern.pattern.startsWith(inputString);
      });

      if (matchingPatterns.length === 0) {
        // Use all patterns for general prediction
        const totalWins = allPatterns.reduce((sum, p) => sum + p.next_win_count, 0);
        const totalLosses = allPatterns.reduce((sum, p) => sum + p.next_loss_count, 0);
        const winProbability = totalWins / (totalWins + totalLosses) * 100;

        return {
          success: true,
          prediction: {
            winProbability: Math.round(winProbability * 100) / 100,
            lossProbability: Math.round((100 - winProbability) * 100) / 100,
            confidenceLevel: 'very_low',
            matchingPatterns: 0
          }
        };
      }

      // Calculate weighted prediction based on matching patterns
      let totalWeightedWins = 0;
      let totalWeightedLosses = 0;
      let totalWeight = 0;

      matchingPatterns.forEach(pattern => {
        const weight = pattern.frequency * (pattern.confidence_score / 100);
        totalWeightedWins += pattern.next_win_count * weight;
        totalWeightedLosses += pattern.next_loss_count * weight;
        totalWeight += weight;
      });

      const winProbability = (totalWeightedWins / (totalWeightedWins + totalWeightedLosses)) * 100;
      const avgConfidence = matchingPatterns.reduce((sum, p) => sum + p.confidence_score, 0) / matchingPatterns.length;

      let confidenceLevel: PatternPrediction['confidence_level'] = 'low';
      if (avgConfidence >= 80) confidenceLevel = 'very_high';
      else if (avgConfidence >= 65) confidenceLevel = 'high';
      else if (avgConfidence >= 50) confidenceLevel = 'medium';
      else if (avgConfidence >= 35) confidenceLevel = 'low';
      else confidenceLevel = 'very_low';

      return {
        success: true,
        prediction: {
          winProbability: Math.round(winProbability * 100) / 100,
          lossProbability: Math.round((100 - winProbability) * 100) / 100,
          confidenceLevel,
          matchingPatterns: matchingPatterns.length
        }
      };

    } catch (error) {
      console.error('Error in predictNextTrade:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get available analysis modes for a file
   */
  static async getAvailableAnalysisModes(fileId: string) {
    const { TradingAnalysisOrchestrator } = await import('@/services/trading-analysis/TradingAnalysisOrchestrator');
    return await TradingAnalysisOrchestrator.getAvailableAnalysisModes(fileId);
  }

  /**
   * Get analysis results for a file and mode
   */
  static async getAnalysisResults(fileId: string, mode: 'basic' | 'intermediate' | 'advanced') {
    const { TradingAnalysisOrchestrator } = await import('@/services/trading-analysis/TradingAnalysisOrchestrator');
    return await TradingAnalysisOrchestrator.getAnalysisResults(fileId, mode);
  }
}

export const tradingPatternAnalysisService = new TradingPatternAnalysisService();